import React, { useMemo, useState } from 'react';
import { formatCents } from '@src/common';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetsTheme as theme } from '@src/core/theme';
import { CartItem } from '@src/core/buyerApiClient';
import { removeCartItem } from '@src/features/cart';

import useTranslate from '@src/i18n/useTranslate';
import OutlinedButton from '@src/components/reusable/button/OutlinedButton';
import ErrorWarning from '../ErrorWarning';
import {
  AdditionalsContainer,
  AdditionalsProductName,
  AdditionalsProductValue,
  AdditionalsTitle,
  CartAdditionals,
  CartProduct,
  Container,
  InformationLabel,
  InformationValue,
  NameContainer,
  ProductImage,
  ProductInformation,
  ProductInformationContainer,
  ProductName,
  ProductProviderName,
  ResourceContainer,
  TariffContainer,
  TariffList,
} from './styles';
import TotalPriceSummary from './TotalPriceSummary';
import { calculateItemTotalPriceSummary, dateBySellingMode } from '@src/common/cart';
import { appInfoSelector } from '@src/features/auth';

interface CartItemProps {
  item: CartItem;
  editable?: boolean;
  showErrors?: boolean;
}

const CartItemContainer = (props: CartItemProps) => {
  const { item, editable = true, showErrors = true } = props;

  const [disabledItem, setDisabledItem] = useState(false);
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const appInfo = useAppSelector(appInfoSelector);

  const [removeItemError, setRemoveItemError] = useState(false);

  const removeItem = async () => {
    try {
      setDisabledItem(true);
      setRemoveItemError(false);
      await dispatch(removeCartItem(item));
    } catch (error) {
      setRemoveItemError(true);
    } finally {
      setDisabledItem(false);
    }
  };

  const mainColor = theme('actionColor');
  const tariffTotalValue = item.tariffs.reduce(
    (total, cartTariff) => total + cartTariff.quantity * cartTariff.costPreview.unitPriceCents,
    0,
  );
  const { total, discount, previousPrice } = useMemo(() => calculateItemTotalPriceSummary(item), [item]);

  return (
    <Container>
      <CartProduct>
        {item.product.thumbnailUrl ? (
          <ProductImage hidden={!item.product.thumbnailUrl}>
            <img src={item.product.thumbnailUrl ?? ''}></img>
          </ProductImage>
        ) : null}

        <ProductInformationContainer data-product-info={item.product.name}>
          <NameContainer>
            <ProductName data-product-name={item.product.name}>{item.product.name}</ProductName>
            {item.product.appId !== appInfo?.id ? (
              <ProductProviderName>
                {i18n('product_package_adder_sold_by', { owner: item.product.app.name })}
              </ProductProviderName>
            ) : null}
          </NameContainer>

          <ProductInformation>
            <InformationLabel>{i18n('cart_selectedDate')}:</InformationLabel>

            <InformationValue>
              <span>
                {dateBySellingMode(
                  item.scheduleDate,
                  item.formattedScheduleTimeMinutes,
                  i18n('account_sale_item_without_date'),
                )}
              </span>
            </InformationValue>
          </ProductInformation>

          <TariffContainer>
            <InformationLabel>{i18n('cart_selectedTariff')}:</InformationLabel>

            <TariffList>
              {item.tariffs.map((cartTariff) => (
                <InformationValue key={cartTariff.tariff.id}>
                  {cartTariff.tariff.type.name} - {cartTariff.quantity}
                </InformationValue>
              ))}
            </TariffList>
          </TariffContainer>

          {item.resources.length > 0 && (
            <ResourceContainer>
              <InformationLabel>{i18n('cart_selectedResources')}:</InformationLabel>

              <TariffList>
                <InformationValue>{item.resources.map(({ resource: { name } }) => name).join(', ')}</InformationValue>
              </TariffList>
            </ResourceContainer>
          )}

          {tariffTotalValue ? (
            <ProductInformation hidden={!tariffTotalValue}>
              <InformationLabel>{i18n('cart_value')}:</InformationLabel>

              <InformationValue>{formatCents(tariffTotalValue + item.costPreview.resourcePriceCents)}</InformationValue>
            </ProductInformation>
          ) : null}
        </ProductInformationContainer>
      </CartProduct>

      {item.additionals.length ? (
        <CartAdditionals hidden={!item.additionals.length}>
          <AdditionalsTitle>{i18n('cart_additionals')}</AdditionalsTitle>

          {item.additionals.map((cartAdditional) => (
            <AdditionalsContainer key={cartAdditional.additional.id}>
              <AdditionalsProductName>{cartAdditional.additional.title}</AdditionalsProductName>

              <AdditionalsProductValue>{formatCents(cartAdditional.amountCents)}</AdditionalsProductValue>

              <AdditionalsProductValue>
                {i18n('cart_quantity')}: {cartAdditional.quantity}
              </AdditionalsProductValue>
            </AdditionalsContainer>
          ))}
        </CartAdditionals>
      ) : null}

      <TotalPriceSummary previousPrice={previousPrice} total={total} discount={discount} />

      {showErrors && item.errors && item.errors.length > 0 ? (
        <ErrorWarning>{i18n('cart_itemErrorWarning')}</ErrorWarning>
      ) : null}

      {Number.isFinite(item.costPreview.productPackageOriginalDiscount) ? (
        <ErrorWarning variant='warning'>{i18n('discount_fallback_warning_message')}</ErrorWarning>
      ) : null}

      {editable ? (
        <OutlinedButton onClick={removeItem} color={mainColor} disabled={disabledItem} showLoadingOnDisabled>
          {i18n('cart_cancel')}
        </OutlinedButton>
      ) : null}

      {removeItemError ? <ErrorWarning>{i18n('cart_item_remove_error')}</ErrorWarning> : null}
    </Container>
  );
};

export default CartItemContainer;
